import React, { useEffect, useState, useRef, useCallback } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import * as PhosphorIcons from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const { Storefront, MusicNotes, Queue, Circle, WifiHigh, WifiSlash, Play, Pause, SkipForward, SkipBack, SpeakerHigh, SpeakerLow, SpeakerSlash, Spinner, FunnelSimple, ArrowsClockwise, MapPin, DeviceMobile, Heartbeat, ShieldCheck, WarningCircle, ChartBar, Timer } = PhosphorIcons;

const formatPlaytime = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0 && m === 0) return s > 0 ? '<1m' : '0m';
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Compact browser/OS hint from user-agent so the dashboard tile stays small.
// We deliberately avoid a full UA parser dependency.
const summariseUserAgent = (ua) => {
  if (!ua) return '';
  const s = String(ua);
  const platform =
    /Android/i.test(s) ? 'Android' :
    /iPhone|iPad|iOS/i.test(s) ? 'iOS' :
    /Windows/i.test(s) ? 'Windows' :
    /Mac OS X/i.test(s) ? 'macOS' :
    /Linux/i.test(s) ? 'Linux' : 'Device';
  const browser =
    /Edg\//i.test(s) ? 'Edge' :
    /Chrome\//i.test(s) ? 'Chrome' :
    /Firefox\//i.test(s) ? 'Firefox' :
    /Safari\//i.test(s) ? 'Safari' : 'Browser';
  return `${platform} · ${browser}`;
};

const StoreTile = ({ store, onCommand, pendingCommand }) => {
  const vol = store.volume ?? 1;
  const VolumeIcon = vol === 0 ? SpeakerSlash : vol < 0.5 ? SpeakerLow : SpeakerHigh;
  const [localVol, setLocalVol] = useState(vol);
  const busy = pendingCommand === store.id;

  const ct = store.current_time || 0;
  const dur = store.duration || 0;
  const progressPct = dur > 0 ? (ct / dur) * 100 : 0;
  const formatTime = (s) => { if (!s || isNaN(s)) return '0:00'; const m = Math.floor(s / 60); return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`; };

  useEffect(() => { setLocalVol(store.volume ?? 1); }, [store.volume]);

  return (
    <div
      className={`bg-white border rounded-xl p-5 transition-all duration-300 shadow-sm ${
        store.is_online ? 'border-[#d9d3c5] hover:border-blue-300' : 'border-[#e8e3d8] opacity-60'
      }`}
      data-testid={`store-tile-${store.id}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${store.is_online ? 'bg-green-500 animate-pulse' : 'bg-[#c4bfb4]'}`} />
          <div className="min-w-0">
            <p className="font-bold text-sm truncate text-[#2d2a26]">{store.name}</p>
            <p className="text-xs text-[#8a8477] font-mono truncate">{store.location}</p>
          </div>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${
          store.is_online ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-[#ede8dc] text-[#a39e92] border border-[#d9d3c5]'
        }`}>
          {store.is_online ? 'Online' : 'Offline'}
        </span>
      </div>

      <div className="bg-[#f5f0e8] rounded-lg p-4 mb-4 min-h-[72px] flex items-center gap-3">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          store.is_playing ? 'bg-blue-100 border border-blue-200' : 'bg-[#ede8dc] border border-[#d9d3c5]'
        }`}>
          {store.is_playing ? (
            <div className="flex items-end gap-[2px] h-5">
              {[1,2,3].map(i => (
                <div key={i} className="w-1 bg-blue-500 rounded-full animate-pulse" style={{ height: `${8 + Math.random()*12}px`, animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          ) : (
            <MusicNotes size={20} className="text-[#a39e92]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {store.current_track ? (
            <>
              <p className="text-sm font-medium truncate text-[#2d2a26]" data-testid={`tile-track-title-${store.id}`}>{store.current_track.title}</p>
              <p className="text-xs text-[#8a8477] truncate">{store.current_track.artist}</p>
            </>
          ) : (
            <p className="text-xs text-[#a39e92] font-mono">{store.is_online ? 'Ready' : 'Offline'}</p>
          )}
        </div>
      </div>

      {store.is_online && store.current_track && (
        <div className="mb-3" data-testid={`progress-bar-${store.id}`}>
          <div className="w-full h-1 bg-[#e8e3d8] rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all duration-[5000ms] ease-linear" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-[#a39e92] font-mono">{formatTime(ct)}</span>
            <span className="text-[10px] text-[#a39e92] font-mono">{formatTime(dur)}</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-3 mb-4">
        <button onClick={() => onCommand(store.id, 'prev')} disabled={!store.is_online || busy}
          className="w-10 h-10 rounded-full bg-[#ede8dc] hover:bg-[#e8e3d8] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
          data-testid={`btn-prev-${store.id}`}>
          {busy ? <Spinner size={16} className="text-[#a39e92] animate-spin" /> : <SkipBack size={16} weight="fill" className="text-[#6b665c]" />}
        </button>
        <button onClick={() => onCommand(store.id, store.is_playing ? 'pause' : 'play')} disabled={!store.is_online || busy}
          className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-[#ede8dc] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all hover:scale-105"
          data-testid={`btn-playpause-${store.id}`}>
          {busy ? <Spinner size={20} className="text-white animate-spin" /> : store.is_playing ? <Pause size={20} weight="fill" className="text-white" /> : <Play size={20} weight="fill" className="text-white ml-0.5" />}
        </button>
        <button onClick={() => onCommand(store.id, 'skip')} disabled={!store.is_online || busy}
          className="w-10 h-10 rounded-full bg-[#ede8dc] hover:bg-[#e8e3d8] disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
          data-testid={`btn-skip-${store.id}`}>
          {busy ? <Spinner size={16} className="text-[#a39e92] animate-spin" /> : <SkipForward size={16} weight="fill" className="text-[#6b665c]" />}
        </button>
      </div>

      <div className="flex items-center gap-2" data-testid={`volume-control-${store.id}`}>
        <VolumeIcon size={14} className="text-[#a39e92] flex-shrink-0" />
        <input type="range" min="0" max="1" step="0.05" value={localVol}
          onChange={(e) => setLocalVol(parseFloat(e.target.value))}
          onMouseUp={() => onCommand(store.id, 'set_volume', { volume: localVol })}
          onTouchEnd={() => onCommand(store.id, 'set_volume', { volume: localVol })}
          disabled={!store.is_online} className="flex-1 h-1 accent-blue-500 disabled:opacity-30"
          data-testid={`volume-slider-${store.id}`} />
        <span className="text-[10px] text-[#a39e92] font-mono w-7 text-right">{Math.round(localVol * 100)}%</span>
      </div>

      {/* Device + location line — small, secondary; only shown when we have data. */}
      {(store.kiosk_user_agent || store.kiosk_geolocation || store.kiosk_ip) && (
        <div className="mt-3 pt-3 border-t border-dashed border-[#e8e3d8] space-y-1" data-testid={`kiosk-device-info-${store.id}`}>
          {store.kiosk_user_agent && (
            <div className="flex items-center gap-1.5 text-[10px] text-[#8a8477] font-mono truncate">
              <DeviceMobile size={12} className="flex-shrink-0" />
              <span className="truncate" title={store.kiosk_user_agent}>{summariseUserAgent(store.kiosk_user_agent)}</span>
            </div>
          )}
          {store.kiosk_geolocation ? (
            <a
              href={`https://www.google.com/maps?q=${store.kiosk_geolocation.lat},${store.kiosk_geolocation.lng}`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-[10px] text-blue-600 hover:text-blue-700 font-mono truncate"
              data-testid={`kiosk-geo-${store.id}`}
            >
              <MapPin size={12} className="flex-shrink-0" />
              <span className="truncate">
                {store.kiosk_geolocation.lat.toFixed(4)}, {store.kiosk_geolocation.lng.toFixed(4)}
                {store.kiosk_geolocation.accuracy ? ` (±${Math.round(store.kiosk_geolocation.accuracy)}m)` : ''}
              </span>
            </a>
          ) : store.kiosk_ip ? (
            <div className="flex items-center gap-1.5 text-[10px] text-[#8a8477] font-mono truncate">
              <MapPin size={12} className="flex-shrink-0" />
              <span>IP {store.kiosk_ip}</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [pendingCommand, setPendingCommand] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [sourceStoreId, setSourceStoreId] = useState('');
  const [health, setHealth] = useState(null);
  const [healthRunning, setHealthRunning] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityDays, setActivityDays] = useState(1);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const commandTimeoutRef = useRef(null);

  const fetchStores = useCallback(async () => {
    try { const res = await apiClient.get('/stores'); setStores(res.data); } catch (e) { console.error('[dashboard] fetch stores failed', e); }
  }, []);

  const connectWebSocket = useCallback(() => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws') + '/api/ws/dashboard-' + Date.now();
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setWsConnected(true);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'store_update') {
            setStores(prev => prev.map(s =>
              s.id === data.store_id ? {
                ...s,
                is_online: data.data?.is_online ?? s.is_online,
                is_playing: data.data?.is_playing ?? s.is_playing,
                current_track: data.data?.current_track ?? s.current_track,
                current_track_index: data.data?.current_track_index ?? s.current_track_index,
                current_time: data.data?.current_time ?? s.current_time,
                duration: data.data?.duration ?? s.duration,
                volume: data.data?.volume ?? s.volume,
                kiosk_user_agent: data.data?.kiosk_user_agent ?? s.kiosk_user_agent,
                kiosk_geolocation: data.data?.kiosk_geolocation ?? s.kiosk_geolocation,
                kiosk_ip: data.data?.kiosk_ip ?? s.kiosk_ip,
                last_update: new Date().toISOString(),
              } : s
            ));
            // Clear pending command when we get status back from the kiosk
            if (data.store_id === pendingCommand) {
              setPendingCommand(null);
              if (commandTimeoutRef.current) clearTimeout(commandTimeoutRef.current);
            }
          }
          // Also handle the remote_command broadcast echo to update UI instantly
          if (data.type === 'remote_command') {
            const storeId = data.store_id;
            setStores(prev => prev.map(s => {
              if (s.id !== storeId) return s;
              if (data.action === 'pause') return { ...s, is_playing: false };
              if (data.action === 'play') return { ...s, is_playing: true };
              return s;
            }));
          }
        } catch (e) { console.error('[dashboard-ws] message handler failed', e); }
      };
      ws.onclose = () => { setWsConnected(false); reconnectTimer.current = setTimeout(connectWebSocket, 3000); };
      ws.onerror = (e) => { console.error('[dashboard-ws] error', e); };
    } catch (e) { console.error('[dashboard-ws] connect failed', e); reconnectTimer.current = setTimeout(connectWebSocket, 3000); }
  }, [pendingCommand]);

  useEffect(() => {
    fetchData();
    connectWebSocket();
    const poll = setInterval(fetchStores, 5000);
    return () => { clearInterval(poll); if (wsRef.current) wsRef.current.close(); if (reconnectTimer.current) clearTimeout(reconnectTimer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [statsRes, storesRes, groupsRes, healthRes] = await Promise.all([
        apiClient.get('/dashboard/stats'), apiClient.get('/stores'), apiClient.get('/store-groups'),
        apiClient.get('/track-health/latest').catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data); setStores(storesRes.data); setGroups(groupsRes.data);
      setHealth(healthRes.data && healthRes.data.ran_at ? healthRes.data : null);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const runHealthCheck = async () => {
    setHealthRunning(true);
    try {
      await apiClient.post('/tracks/verify');
      const res = await apiClient.get('/track-health/latest');
      setHealth(res.data && res.data.ran_at ? res.data : null);
      toast.success('Track health check complete');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setHealthRunning(false); }
  };

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiClient.get(`/analytics/kiosk-stats?days=${activityDays}`)
        .then(res => { if (!cancelled) setActivity(res.data); })
        .catch(() => {});
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [activityDays]);

  const sendCommand = async (storeId, action, extra = {}) => {
    if (pendingCommand) return;
    setPendingCommand(storeId);
    try {
      await apiClient.post(`/stores/${storeId}/remote-control`, { action, ...extra });
      commandTimeoutRef.current = setTimeout(() => { setPendingCommand(null); fetchStores(); }, 3000);
    } catch (error) { toast.error(formatApiError(error)); setPendingCommand(null); }
  };

  const openSyncDialog = () => {
    if (selectedGroup === 'all') return;
    // Preselect a sensible default: the first online store in the group that
    // is currently playing something. Fall back to any online store.
    const groupStores = stores.filter(s => s.group_id === selectedGroup);
    const preferred =
      groupStores.find(s => s.is_online && s.is_playing && s.current_track) ||
      groupStores.find(s => s.is_online) ||
      groupStores[0];
    setSourceStoreId(preferred?.id || '');
    setSyncOpen(true);
  };

  const handleSyncGroup = async () => {
    if (selectedGroup === 'all' || syncing) return;
    setSyncing(true);
    try {
      const body = sourceStoreId
        ? { action: 'change_track', source_store_id: sourceStoreId }
        : { action: 'play' };
      const res = await apiClient.post(`/store-groups/${selectedGroup}/sync`, body);
      toast.success(res.data.message);
      setSyncOpen(false);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSyncing(false); }
  };

  if (loading) return <div className="text-[#8a8477] font-mono p-8">Loading command center...</div>;

  const filteredStores = selectedGroup === 'all' ? stores : stores.filter(s => s.group_id === selectedGroup);
  const onlineCount = filteredStores.filter(s => s.is_online).length;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-[#2d2a26] mb-2" data-testid="dashboard-title">Command Center</h1>
          <p className="text-[#8a8477] font-mono text-sm tracking-wider uppercase">Live Control &amp; Monitoring</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono ${
          wsConnected ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-[#ede8dc] text-[#a39e92] border border-[#d9d3c5]'
        }`} data-testid="ws-status-badge">
          {wsConnected ? <WifiHigh size={14} /> : <WifiSlash size={14} />}
          {wsConnected ? 'Live' : 'Connecting...'}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Stores" value={stats?.total_stores || 0} icon={<Storefront size={20} className="text-blue-500" />} testId="stat-stores" />
        <StatCard label="Online" value={onlineCount} valueClass="text-green-600" icon={<Circle size={20} weight="fill" className="text-green-500 animate-pulse" />} testId="stat-online" />
        <StatCard label="Tracks" value={stats?.total_tracks || 0} icon={<MusicNotes size={20} className="text-blue-500" />} testId="stat-tracks" />
        <StatCard label="Playlists" value={stats?.total_playlists || 0} icon={<Queue size={20} className="text-blue-500" />} testId="stat-playlists" />
      </div>

      <TrackHealthCard health={health} onRunNow={runHealthCheck} running={healthRunning} />

      <div className="bg-gradient-to-r from-[#e4ebe5] to-[#f5f0e8] border border-[#c5d4c8] rounded-md p-5 flex items-center justify-between gap-4" data-testid="dashboard-book-demo-card">
        <div>
          <h3 className="text-sm font-bold text-[#2d2a26] mb-0.5">Need a walkthrough?</h3>
          <p className="text-xs text-[#6b665c] font-mono">Book a 30-min live demo with our team — tailored to your chain.</p>
        </div>
        <a href={process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/your-demo-link'} target="_blank" rel="noreferrer" data-testid="dashboard-book-demo-link">
          <button type="button" className="bg-[#7c9082] hover:bg-[#6a7e71] text-white border-0 rounded-md px-4 py-2 text-sm font-medium transition-colors">
            Book a Demo
          </button>
        </a>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#2d2a26] flex items-center gap-2">
            <Circle size={8} weight="fill" className="text-red-500 animate-pulse" />Store Players
          </h2>
          <div className="flex items-center gap-3">
            {/* Group Filter */}
            <div className="flex items-center gap-2" data-testid="group-filter">
              <FunnelSimple size={14} className="text-[#8a8477]" />
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="text-xs font-mono bg-white border border-[#c5d4c8] rounded-lg px-3 py-1.5 text-[#2d2a26] focus:outline-none focus:ring-1 focus:ring-[#7c9082]"
                data-testid="group-filter-select"
              >
                <option value="all">All Groups</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            {/* Sync Button */}
            {selectedGroup !== 'all' && (
              <button
                onClick={openSyncDialog}
                disabled={syncing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-[#7c9082] hover:bg-[#6b7f71] text-white rounded-lg transition-all disabled:opacity-50"
                data-testid="sync-group-btn"
              >
                <ArrowsClockwise size={14} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing...' : 'Sync Group'}
              </button>
            )}
            <span className="text-xs text-[#8a8477] font-mono">{onlineCount}/{filteredStores.length} online</span>
          </div>
        </div>

        {filteredStores.length === 0 ? (
          <div className="bg-white border border-[#d9d3c5] rounded-xl p-12 text-center shadow-sm">
            <Storefront size={48} className="text-[#c4bfb4] mx-auto mb-4" />
            <p className="text-[#8a8477] font-mono text-sm">{selectedGroup === 'all' ? 'No stores configured.' : 'No stores in this group.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredStores.map(store => <StoreTile key={store.id} store={store} onCommand={sendCommand} pendingCommand={pendingCommand} />)}
          </div>
        )}
      </div>

      {/* Kiosk Activity analytics — playtime, stops, refreshes, closes */}
      <div data-testid="kiosk-activity-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#2d2a26] flex items-center gap-2">
            <ChartBar size={16} className="text-[#7c9082]" />Kiosk Activity
          </h2>
          <select
            value={activityDays}
            onChange={(e) => setActivityDays(Number(e.target.value))}
            className="text-xs font-mono bg-white border border-[#c5d4c8] rounded-lg px-3 py-1.5 text-[#2d2a26] focus:outline-none focus:ring-1 focus:ring-[#7c9082]"
            data-testid="kiosk-activity-days"
          >
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
        <div className="bg-white border border-[#d9d3c5] rounded-xl overflow-hidden shadow-sm">
          {!activity || activity.stores.length === 0 ? (
            <p className="text-xs text-[#8a8477] font-mono p-6 text-center">No kiosk activity data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f5f0e8] border-b border-[#d9d3c5]">
                  <tr>
                    <th className="px-5 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-[#6b665c]">Store</th>
                    <th className="px-5 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-[#6b665c]">
                      <span className="inline-flex items-center gap-1"><Timer size={12} /> Playtime</span>
                    </th>
                    <th className="px-5 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-[#6b665c]">Uptime</th>
                    <th className="px-5 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-[#6b665c]">Music Stops</th>
                    <th className="px-5 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-[#6b665c]">Page Refreshes</th>
                    <th className="px-5 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-[#6b665c]">Window Closes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f0ead9]">
                  {activity.stores.map((s) => (
                    <tr key={s.store_id} data-testid={`kiosk-activity-row-${s.store_id}`}>
                      <td className="px-5 py-3 font-medium text-[#2d2a26] truncate max-w-[220px]">{s.store_name}</td>
                      <td className="px-5 py-3 font-mono text-blue-700" data-testid={`activity-playtime-${s.store_id}`}>{formatPlaytime(s.playtime_seconds)}</td>
                      <td className="px-5 py-3" data-testid={`activity-uptime-${s.store_id}`}>
                        {s.uptime_pct === null || s.uptime_pct === undefined ? (
                          <span className="text-xs font-mono text-[#8a8477]">—</span>
                        ) : (
                          <span
                            title={`Playtime vs open hours (${s.open_time}–${s.close_time}). Set hours on the Stores page.`}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono cursor-help border ${
                              s.uptime_pct >= 90
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : s.uptime_pct >= 60
                                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                                  : 'bg-red-50 text-red-700 border-red-200'
                            }`}
                          >
                            {s.uptime_pct}%
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 font-mono" data-testid={`activity-stops-${s.store_id}`}>
                        <span className={s.stops > 0 ? 'text-amber-700' : 'text-[#8a8477]'}>{s.stops}</span>
                      </td>
                      <td className="px-5 py-3 font-mono" data-testid={`activity-refreshes-${s.store_id}`}>
                        <span className={s.refreshes > 0 ? 'text-amber-700' : 'text-[#8a8477]'}>{s.refreshes}</span>
                      </td>
                      <td className="px-5 py-3 font-mono" data-testid={`activity-closes-${s.store_id}`}>
                        <span className={s.closes > 0 ? 'text-red-600' : 'text-[#8a8477]'}>{s.closes}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Sync Group dialog — pick a source kiosk; others jump to its track */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26] max-w-md" data-testid="sync-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <ArrowsClockwise size={18} className="text-[#7c9082]" />
              Sync group players
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#6b665c]">
              Choose a source kiosk. Every other kiosk in <strong>{groups.find(g => g.id === selectedGroup)?.name || 'this group'}</strong> will jump to the same track and start playing.
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {stores.filter(s => s.group_id === selectedGroup).map(s => (
                <label
                  key={s.id}
                  className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-all ${
                    sourceStoreId === s.id ? 'border-[#7c9082] bg-[#e4ebe5]' : 'border-[#d9d3c5] hover:bg-[#f5f0e8]'
                  }`}
                  data-testid={`sync-source-option-${s.id}`}
                >
                  <input
                    type="radio"
                    name="sync-source"
                    value={s.id}
                    checked={sourceStoreId === s.id}
                    onChange={() => setSourceStoreId(s.id)}
                    className="accent-[#7c9082]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${s.is_online ? 'bg-green-500' : 'bg-[#c4bfb4]'}`} />
                      <p className="font-medium text-sm truncate text-[#2d2a26]">{s.name}</p>
                    </div>
                    <p className="text-xs text-[#8a8477] font-mono truncate">
                      {s.current_track ? `${s.current_track.title} — ${s.current_track.artist || 'Unknown'}` : (s.is_online ? 'Online, no track' : 'Offline')}
                    </p>
                  </div>
                </label>
              ))}
              {stores.filter(s => s.group_id === selectedGroup).length === 0 && (
                <p className="text-xs text-[#8a8477] font-mono text-center py-4">No kiosks in this group.</p>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setSyncOpen(false)} disabled={syncing} data-testid="sync-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSyncGroup}
                disabled={syncing || !sourceStoreId}
                className="bg-[#7c9082] hover:bg-[#6a7e71] text-white"
                data-testid="sync-confirm-btn"
              >
                {syncing ? 'Syncing...' : 'Sync now'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TrackHealthCard = ({ health, onRunNow, running }) => {
  const broken = health?.broken_tracks || [];
  const allGood = health && health.broken === 0;
  return (
    <div
      className={`bg-white border rounded-md overflow-hidden ${
        !health ? 'border-[#d9d3c5]' : allGood ? 'border-emerald-200' : 'border-red-200'
      }`}
      data-testid="track-health-card"
    >
      <div className={`px-5 py-3 flex items-center justify-between gap-3 border-b ${
        !health ? 'bg-[#f5f0e8] border-[#d9d3c5]' : allGood ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-center gap-2 min-w-0">
          <Heartbeat size={18} className={!health ? 'text-[#8a8477]' : allGood ? 'text-emerald-600' : 'text-red-600'} />
          <h3 className="text-sm font-bold text-[#2d2a26]">Track Health</h3>
          {health && (
            <span className="text-xs font-mono text-[#8a8477] truncate" data-testid="track-health-ran-at">
              {health.trigger === 'nightly' ? 'nightly check' : 'manual check'} · {new Date(health.ran_at).toLocaleString()}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onRunNow}
          disabled={running}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-[#7c9082] hover:bg-[#6b7f71] text-white rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          data-testid="track-health-run-now"
        >
          <ArrowsClockwise size={13} className={running ? 'animate-spin' : ''} />
          {running ? 'Checking…' : 'Run now'}
        </button>
      </div>
      <div className="px-5 py-4">
        {!health ? (
          <p className="text-xs text-[#8a8477] font-mono">
            No health check yet. It runs automatically every night and flags songs your kiosks can't play — or run it now.
          </p>
        ) : allGood ? (
          <p className="text-sm text-emerald-700 flex items-center gap-2" data-testid="track-health-all-good">
            <ShieldCheck size={16} /> All {health.total_checked} Drive track(s) are playable.
          </p>
        ) : (
          <div className="space-y-2" data-testid="track-health-broken-list">
            <p className="text-sm text-red-700 font-medium">
              {health.broken} of {health.total_checked} track(s) are unplayable — kiosks skip these:
            </p>
            <div className="divide-y divide-[#f0ead9] max-h-48 overflow-y-auto">
              {broken.map((b) => (
                <div key={b.id} className="py-1.5 flex items-center justify-between gap-4 text-sm" title={b.detail}>
                  <span className="font-medium truncate text-[#2d2a26] flex items-center gap-1.5">
                    <WarningCircle size={14} className="text-red-500 flex-shrink-0" />{b.title}
                  </span>
                  <span className="text-xs font-mono text-red-600 flex-shrink-0">{String(b.status).replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
            <a href="/dashboard/library" className="inline-block text-xs font-mono text-blue-600 hover:underline" data-testid="track-health-fix-link">
              Open Music Library to fix (Re-auth) →
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, valueClass = '', icon, testId }) => (
  <div className="bg-white border border-[#d9d3c5] rounded-lg p-4 shadow-sm" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[#8a8477] font-mono text-[10px] uppercase tracking-wider mb-1">{label}</p>
        <p className={`text-2xl font-bold font-mono text-[#2d2a26] ${valueClass}`}>{value}</p>
      </div>
      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">{icon}</div>
    </div>
  </div>
);

export default Dashboard;
